import { Button, ButtonGroup } from '@material-ui/core'
import { isEmpty } from 'lodash'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  cancelPendingPSACAdmissionModification,
  fetchPreschoolAcademicYears,
  generatePSACTermInvoice,
  previewPSACTermInvoice,
  releasePSACSeat,
  terminatePSACEnrolment
} from 'api'
import BaseEntity from 'app/components/BaseEntity'
import { useStyles } from 'app/components/generic/Button'
import {
  programActions,
  tableColumnDefaultOption
} from 'app/config'
import constants from 'app/constants'
import { confirm, eventAlert, eventEmitter } from 'app/helpers'
import {
  fetchExcelEnrolmentData,
  fetchPsAcEnrolmentData,
  updateClassData
} from 'app/store/actions/classEntity'

import { columns } from './config'
import ModifyAdmissionModal from './ModifyAdmissionModal'

import MasterModal from '../components/MasterModal'

const PSACEnrolment = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [action, setAction] = React.useState('')
  const [cancelId, setCancelId] = React.useState('')
  const [editIndex, setEditIndex] = React.useState(null)
  // Feature 2 — modify-admission modal lives alongside the terminate modal.
  const [isModifyModalOpen, setIsModifyModalOpen] = React.useState(false)
  const [modifyTargetIndex, setModifyTargetIndex] = React.useState(null)
  const psacEnrolmentData = useSelector(
    (state) => state.classEntityReducer.psacEnrolmentData
  )
  const dispatch = useDispatch()

  const [academicYears, setAcademicYears] = React.useState([])

  React.useEffect(() => {
    ;(async () => {
      try {
        const years = await fetchPreschoolAcademicYears()
        const yearsArray = years?.results
        const yearsMapping =
          yearsArray.length > 0
            ? yearsArray.reduce((acc, item) => {
                acc[item.uuid] = item.name
                return acc
              }, {})
            : {}

        setAcademicYears(yearsMapping)
      } catch (error) {
        console.error(error)
      }
    })()
  }, [])

  const openModal = React.useCallback((action, dataIndex) => {
    setIsModalOpen(true)
    setAction(action)
    setEditIndex(dataIndex)
  }, [])

  const closeModal = () => {
    setIsModalOpen(false)
  }

  // Feature 5 — predicate: is this row a seat-blocked admission?
  // Mirror the backend filter — active, admission_fee_paid, first
  // instalment PENDING. The list endpoint serializes
  // `student_admission_installments` (see queryset.prefetch_related on
  // StudentAdmissionAPI); the sequence=1 row is the canonical signal.
  const isSeatBlockRow = (row) => {
    if (!row) return false
    if (row.status !== 'active') return false
    if (!row.admission_fee_paid) return false
    const installments = row.student_admission_installments || []
    const first = installments.find((i) => i.sequence === 1)
    return Boolean(first) && first.status === 'pending'
  }

  const onActionButtonClick = async (e, action, dataIndex) => {
    if (action === 'terminate_enrolment') {
      openModal('terminate_enrolment', dataIndex)
    } else if (action === 'modify_admission') {
      setModifyTargetIndex(dataIndex)
      setIsModifyModalOpen(true)
    } else if (action === 'generate_term_invoice') {
      // Feature 5 — Generate Term Fee Invoice for a seat-blocked
      // admission. Charmi 2026-05-28 item 2.3 — first fetch a payment
      // summary and show it for review, then generate on confirm.
      // Backend is idempotent; payment link comes back in the response.
      const row = psacEnrolmentData?.data?.[dataIndex] || {}
      const refresh = () => {
        const queries = JSON.parse(localStorage.getItem('filterQueries'))
        dispatch(
          fetchExcelEnrolmentData(
            { ...queries },
            false,
            false,
            constants.PSAC_ENROLMENT
          )
        )
      }
      const doGenerate = async () => {
        try {
          const result = await generatePSACTermInvoice({
            studentAdmissionUuid: row.uuid
          })
          eventAlert(
            result?.payment_link
              ? `Term Fee invoice generated — link: ${result.payment_link}`
              : 'Term Fee invoice generated',
            constants.SUCCESS
          )
          refresh()
        } catch (error) {
          eventAlert(
            error?.message || 'Failed to generate Term Fee invoice',
            constants.ERROR
          )
        }
      }
      try {
        const preview = await previewPSACTermInvoice({
          studentAdmissionUuid: row.uuid
        })
        const lines = (preview?.payment_summary?.[0]?.line_items || [])
          .map((li) => `  ${li.label}: ₹${Math.round(li.value)}`)
          .join('\n')
        confirm(
          doGenerate,
          `Term fee payment summary:\n${lines}\n\nGenerate this invoice?`,
          () => {},
          { confirmButtonText: 'Generate invoice' }
        )
      } catch (error) {
        // Preview failed — fall back to direct generate so ops isn't blocked.
        eventAlert(
          (error?.message || 'Preview unavailable') + ' — generating directly.',
          constants.ERROR
        )
        doGenerate()
      }
    } else if (action === 'cancel_pending_modification') {
      // Phase 3 (Akshay 2026-05-27) — cancel an awaiting_payment
      // PendingAdmissionModification. Voids the differential Invoice;
      // no admission state to roll back since the request flow defers
      // mutation until payment.
      const row = psacEnrolmentData?.data?.[dataIndex] || {}
      const pending = row?.pending_admission_modification
      if (!pending?.invoice_uuid) {
        eventAlert('No pending upgrade on this row.', constants.ERROR)
        return
      }
      confirm(
        async () => {
          try {
            await cancelPendingPSACAdmissionModification({
              studentAdmissionUuid: row.uuid,
              invoiceUuid: pending.invoice_uuid
            })
            eventAlert(
              'Pending upgrade cancelled. Differential invoice voided.',
              constants.SUCCESS
            )
            const queries = JSON.parse(localStorage.getItem('filterQueries'))
            dispatch(
              fetchExcelEnrolmentData(
                { ...queries },
                false,
                false,
                constants.PSAC_ENROLMENT
              )
            )
          } catch (error) {
            eventAlert(
              error?.message || 'Failed to cancel pending upgrade',
              constants.ERROR
            )
          }
        },
        'This will cancel the pending upgrade and void the differential invoice. Continue?',
        () => {},
        { confirmButtonText: 'Cancel upgrade' }
      )
    } else if (action === 'release_seat') {
      // Feature 5 — Release Seat. Confirmation alert, then terminate.
      const row = psacEnrolmentData?.data?.[dataIndex] || {}
      if (
        !window.confirm(
          'This will release the batch seat and terminate the admission. ' +
            'AIM (₹18,000) is non-refundable. Continue?'
        )
      ) {
        return
      }
      try {
        await releasePSACSeat({
          studentAdmissionUuid: row.uuid,
          notes: 'Seat block release via Alfred'
        })
        eventAlert('Seat released — admission terminated.', constants.SUCCESS)
        const queries = JSON.parse(localStorage.getItem('filterQueries'))
        dispatch(
          fetchExcelEnrolmentData(
            { ...queries },
            false,
            false,
            constants.PSAC_ENROLMENT
          )
        )
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to release seat',
          constants.ERROR
        )
      }
    }
  }

  const onSaveClick = async (newData, action) => {
    const { uuid: enrolmentUuid } = psacEnrolmentData.data[editIndex]
    try {
      if (action === 'terminate_enrolment') {
        await terminatePSACEnrolment({
          student_admission_uuid: enrolmentUuid,
          notes: newData.notes,
          endDate: newData.end_date,
          reason: newData.reason
        })

        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        eventAlert('Enrolment Terminated Successfully', constants.SUCCESS)
        closeModal()
      }

      const queries = JSON.parse(localStorage.getItem('filterQueries'))
      dispatch(
        fetchExcelEnrolmentData(
          { ...queries },
          false,
          false,
          constants.PSAC_ENROLMENT
        )
      )
    } catch (error) {
      console.log(error)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR, error)
      eventAlert(error.message, constants.ERROR)
    }
  }
  const classes = useStyles()
  const modifiedColumns = React.useMemo(() => {
    return [
      ...columns,
      {
        name: 'program_fee.academic_year.name',
        label: 'Academic year',
        widget: 'select',
        widgetFilter: true,
        valueLabelMappings: academicYears,
        disabledInForm: true,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'academic_year'
        },
      },
      {
        name: '',
        label: 'actions',
        disabledInForm: true,
        options: {
          disableFilter: true,
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const { status, is_termination_requested } =
              psacEnrolmentData?.data[dataIndex] || {}

            const row = psacEnrolmentData?.data[dataIndex] || {}
            // Feature 2 — modify_admission is SCHOOL+ACTIVE only. The
            // backend re-checks programme type; this guards the UI affordance.
            const programType = row?.program_fee?.program?.type
            return (
              status !== 'terminated' &&
              status !== 'expired' && (
                <ButtonGroup>
                  {Object.keys(programActions).map((action, index) => {
                    // Modify-admission feature temporarily hidden
                    // (2026-06-10) — re-enable once the deferred-commit
                    // flow is fixed.
                    if (
                      action === 'modify_admission' ||
                      action === 'cancel_pending_modification'
                    ) {
                      return null
                    }
                    if (
                      action === 'terminate_enrolment' &&
                      is_termination_requested
                    ) {
                      return null
                    }
                    if (
                      action === 'modify_admission' &&
                      (programType !== 'school' || status !== 'active')
                    ) {
                      return null
                    }
                    // Phase 3 — cancel_pending_modification only when
                    // there's an awaiting_payment PendingAdmissionModification
                    // on this row. Hide modify_admission concurrently
                    // (the BE rejects a second modify while one is
                    // already in flight, so showing it is misleading).
                    const hasPendingMod =
                      row?.pending_admission_modification?.status ===
                      'awaiting_payment'
                    if (
                      action === 'cancel_pending_modification' &&
                      !hasPendingMod
                    ) {
                      return null
                    }
                    if (action === 'modify_admission' && hasPendingMod) {
                      return null
                    }
                    // Feature 5 — seat-block actions only on
                    // seat-blocked admissions (Term 1 PENDING).
                    if (
                      (action === 'generate_term_invoice' ||
                        action === 'release_seat') &&
                      !isSeatBlockRow(row)
                    ) {
                      return null
                    }
                    return (
                      <Button
                        key={'ExcelEnrolment' + index}
                        title={programActions[action]}
                        className={classes.button}
                        onClick={(e) => {
                          onActionButtonClick(e, action, dataIndex)
                        }}
                      >
                        {programActions[action]}
                      </Button>
                    )
                  })}
                </ButtonGroup>
              )
            )
          }
        }
      }
    ]
  }, [psacEnrolmentData])

  console.log(psacEnrolmentData);
  
  return (
    <>
      <BaseEntity
        entity={constants.PSAC_ENROLMENT}
        label={constants.PSAC_ENROLMENT_LABEL}
        columns={modifiedColumns}
        data={psacEnrolmentData}
        fetchData={fetchPsAcEnrolmentData}
        updateEntityData={updateClassData}
        createDependency
        disableDelete
        readOnly
      />
      <MasterModal
        isModalOpen={isModalOpen}
        onSaveClick={onSaveClick}
        closeModal={closeModal}
        action={action}
        data={psacEnrolmentData.data[editIndex] || {}}
        onActionButtonClick={onActionButtonClick}
        setCancelId={setCancelId}
        isProgramEnrolment={true}
      />
      <ModifyAdmissionModal
        open={isModifyModalOpen}
        admission={
          modifyTargetIndex != null
            ? psacEnrolmentData.data[modifyTargetIndex]
            : null
        }
        onClose={() => {
          setIsModifyModalOpen(false)
          setModifyTargetIndex(null)
          const queries = JSON.parse(localStorage.getItem('filterQueries'))
          dispatch(
            fetchExcelEnrolmentData(
              { ...queries },
              false,
              false,
              constants.PSAC_ENROLMENT
            )
          )
        }}
      />
    </>
  )
}

export default PSACEnrolment
